import React from 'react';
import { IEntity } from '@opengov/og-api-ts';
import { ICurrentOrLegacySavedView } from '@opengov/reporting-legacy-module';
import { ICurrentOrLegacyReport, IEmbedDataV3 } from '@opengov/reporting-types';
import { IPublication, IStory, IStoryContent, IUpdatePayload } from '@opengov/stories-shared/types';

import { IVisualizationSelectOption } from '../../../stories/utils';
import { ToastProps } from '../../../stories/components/Toast/Toast';
import {
  ICurrentOrLegacySavedViewInfo,
  IDeleteStoryEmbedResponse,
  IEmbed,
  IHandleEmbedPayload,
  IModalMessages,
  ISessionUser,
  ISettings,
  IStoryEmbedDeletePayload,
  IStoryEmbedsResponse,
  IUpdateStoryContentProps
} from '../../../types';
import { IEmbedCreateEmbedPayload } from './types';

import { AddTileOld } from './AddTileOld';
import { AddTileNew } from './AddTileNew';

export interface IAddTileProps {
  isTemplate: boolean;
  isAdmin: boolean;
  isRequestingReports: boolean;
  isRequestingSavedViews: boolean;
  isRequestingSavedViewInfo: boolean;
  isCreatingEmbed: boolean;
  hasEmbedError: boolean;
  hasGallery: boolean;
  canUseV3Embeds: boolean;
  embedV2EditingEnabled: boolean;
  entity: IEntity;
  entityId: number;
  entitySubdomain: string;
  settings?: ISettings;
  story: IStory;
  storyContent: IStoryContent;
  storyContents: IStoryContent[];
  reports: ICurrentOrLegacyReport[];
  savedViews: ICurrentOrLegacySavedView[];
  savedViewInfo: ICurrentOrLegacySavedViewInfo;
  groupDataBy: string;
  selectedGroupings: string[];
  embed: IEmbed;
  messages: IModalMessages;
  visualizationSelectOptions: IVisualizationSelectOption[];
  isReportEmbedListSelected: boolean;
  user: ISessionUser;
  selectedEmbed: IStoryEmbedsResponse;
  publication?: IPublication;
  displayReportEmbeds?: boolean;
  isFetchingEmbed?: boolean;
  accountSummaryChartsInEmbedEnabled?: boolean;
  useV3EmbedsForChartAndDetailsReports?: boolean;
  useNewAddTileUI?: boolean;
  accountSummaryChartTitleAndDescriptionEnabled?: boolean;
  onEmbedChange(embed: any): void;
  onEmbedError(): void;
  unsetIsCreatingEmbed(): void;
  getEmbed(props: { id: string }): Promise<IEmbed>;
  onClose(): void;
  resetEmbed(): void;
  resetReports(): void;
  resetSavedViews(): void;
  resetSavedViewInfo(): void;
  resetGroupDataBy(): void;
  resetSelectedGroupings(): void;
  getReports(props: {
    entityId: number;
    includeMultiDatasetReports?: boolean;
  }): Promise<ICurrentOrLegacyReport[]>;
  getSavedViews(props: { reportId?: string; legacyReportId?: string; reportVersion: string; entity: IEntity }): any;
  getSavedViewInfo(props: { savedViewId: string }): void;
  setGroupDataBy(groupDataBy: string): void;
  setSelectedGroupings(categories: string[]): void;
  cloneEmbedById(props: { id: string; embedVersion: string }): any;
  deleteStoryEmbed(props: IUpdatePayload): Promise<IDeleteStoryEmbedResponse>;
  deleteReportEmbeds(props: IStoryEmbedDeletePayload): Promise<void>;
  createEmbed(props: IEmbedCreateEmbedPayload): Promise<IEmbed>;
  createV3Embed(
    props: {
      id?: string;
      title: string;
      description: string;
      reportId: string;
      savedViewId: string;
      shouldSync: boolean;
      reportVersion: string;
      type: string;
      data?: IEmbedDataV3;
    },
    addOrUpdateEmbed?: boolean
  );
  createV4Embed(
    props: {
      id?: string;
      title: string;
      description: string;
      reportId: string;
      savedViewId: string;
      shouldSync: boolean;
      reportVersion: string;
      type: string;
      data?: IEmbedDataV3;
    },
    addOrUpdateEmbed?: boolean
  );
  previewEmbed(props): void;
  handleStoryEmbeds(props: IHandleEmbedPayload): void;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(storyContent: IUpdateStoryContentProps): void;
  getStoryContent(payload: {
    storyId: string;
    entityId: number;
    entitySubdomain: string;
    isTemplate: boolean;
  }): Promise<IStoryContent[]>;
  toggleToast(toastProps: ToastProps | null): void;
}

/**
 * AddTile - A wrapper component that conditionally renders either AddTileOld or AddTileNew
 * based on the useNewAddTileUI prop or other conditions.
 */
export class AddTile extends React.Component<IAddTileProps> {
  private shouldUseNewUI(): boolean {
    const { useNewAddTileUI } = this.props;

    // If explicitly set via prop, use that
    if (typeof useNewAddTileUI === 'boolean') {
      return useNewAddTileUI;
    }

    return false;
  }

  render() {
    const { useNewAddTileUI: _useNewAddTileUI, ...passThroughProps } = this.props;
    const useNewUI = this.shouldUseNewUI();

    if (useNewUI) {
      return <AddTileNew {...passThroughProps} />;
    } else {
      return <AddTileOld {...passThroughProps} />;
    }
  }
}
